import { type MessageDescriptor, i18n } from '@lingui/core'
import { msg } from '@lingui/core/macro'

export type FriendlyError = {
  title: string
  message: string
}

// Single source of truth: define the error codes once
export const KNOWN_ERROR_CODES = [
  'TIMEOUT',
  'NOT_AVAILABLE',
  'NO_ARTICLES',
  'INVALID_URLS',
  'REJECTED_BY_GUARDS',
  'CONTENT_FILTER',
] as const

// Derive the type from the actual data
export type KnownErrorCode = (typeof KNOWN_ERROR_CODES)[number]

// Single source of truth: map backend error codes → friendly copy
type FriendlyErrorDescriptors = {
  title: MessageDescriptor
  message: MessageDescriptor
}
const ERROR_CODE_DESCRIPTORS: Record<KnownErrorCode, FriendlyErrorDescriptors> =
  {
    TIMEOUT: {
      title: msg`Search took too long`,
      message: msg`Our knowledge search timed out. Please try again in a moment.`,
    },
    NOT_AVAILABLE: {
      title: msg`Search is temporarily unavailable`,
      message: msg`We're having trouble reaching the knowledge source. Please try again shortly.`,
    },
    NO_ARTICLES: {
      title: msg`No matching articles found`,
      message: msg`We couldn't find relevant articles for your question. Try rephrasing or ask about a different topic.`,
    },
    INVALID_URLS: {
      title: msg`Unverified links filtered`,
      message: msg`Response contained unveriified link and has been filtered out. Please try rephrasing your question.`,
    },
    REJECTED_BY_GUARDS: {
      title: msg`Request blocked`,
      message: msg`This question was blocked by safety filters. Please rephrase or ask something else.`,
    },
    CONTENT_FILTER: {
      title: msg`Response blocked by safety filter`,
      message: msg`Our AI provider's safety filter prevented generating a response. This can happen if the question triggered content policies. Try rephrasing your question`,
    },
  }

export const isKnownErrorCode = (code: string): code is KnownErrorCode => {
  return KNOWN_ERROR_CODES.some((knownCode) => knownCode === code)
}

// Lookup via the single map without casts
export const getFriendlyError = (
  code: string,
  fallbackMessage?: string | null,
): FriendlyError => {
  if (isKnownErrorCode(code)) {
    const d = ERROR_CODE_DESCRIPTORS[code]
    return { title: i18n._(d.title), message: i18n._(d.message) }
  }
  return {
    title: i18n._(msg`Something went wrong`),
    message:
      fallbackMessage ||
      i18n._(msg`Please try again. If the issue persists, contact support.`),
  }
}
